import { useEffect, useState } from 'react';
import type { UserTheme } from '@/types';
import type { ResolvedTheme } from './antdTheme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * WEB-1024: сохранённый выбор темы — тот же ключ, что пишет App.tsx и читает
 * загрузочный скрипт index.html (WEB-806). Ничего не бросает: приватный режим
 * и заблокированное хранилище просто дают «нет выбора».
 */
function storedTheme(): 'light' | 'dark' | null {
  try {
    const stored = localStorage.getItem('ui-theme');
    return stored === 'dark' || stored === 'light' ? stored : null;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    // WEB-3826: пустой ответ (заглушка без ответа) — «тема устройства неизвестна».
    Boolean(window.matchMedia(DARK_QUERY)?.matches)
  );
}

/**
 * Разрешает выбор пользователя (`system` | `light` | `dark`) в конкретный режим.
 * Для `system` — подписка на смену темы устройства, чтобы приложение подхватывало
 * её вживую БЕЗ перезагрузки страницы (WEB-386 PM-4).
 */
export function useResolvedTheme(userTheme: UserTheme | undefined): ResolvedTheme {
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY) as MediaQueryList | undefined;
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // WEB-1024: профиль приезжает по сети, и до его прихода userTheme === undefined.
  // Раньше это молча означало «системная» — на машине со светлой системой
  // кабинет отрисовывался светлым, хотя в настройках стоит тёмная, и таким
  // оставался (заметнее всего при дубликате вкладки). Загрузочный скрипт
  // index.html к этому моменту УЖЕ поставил верную тему из сохранённого выбора
  // (WEB-806) — приложение её перебивало. Порядок при неизвестном профиле:
  // сохранённый выбор → системная. Пришедший профиль главнее обоих.
  const effective = userTheme ?? storedTheme() ?? 'system';
  if (effective === 'dark') return 'dark';
  if (effective === 'light') return 'light';
  return systemDark ? 'dark' : 'light';
}
